import React, { useState } from 'react';
import { Box } from '@mui/material';
import {
    RadioButtonGroupInput,
    TextInput,
    BooleanInput,
    ReferenceInput,
    AutocompleteInput,
    required,
    maxLength,
    FileInput,
    FileField,
    useRecordContext,
    useTranslate,
} from 'react-admin';
import { useFormContext, useWatch } from 'react-hook-form';
import { noSpecialCharacters } from '@/components/global/validators/customValidators.jsx';
import { groupFilterToQuery, aliasFilterToQuery, contractorFilterToQuery } from '@/helpers/filters/filterToQuery';
import PropTypes from 'prop-types';

const DeviceGroupInput = ({ bulk = false, defaultContractorId = null }) => {
    const [newGroup, setNewGroup] = useState(false);
    const contractorId = useWatch({ name: 'contractorId' });
    const { setValue, resetField } = useFormContext();
    const record = useRecordContext();
    const translate = useTranslate();

    const defaultGroupValues = {
        name: '',
        targetSoftwareMcuId: null,
        targetSoftwareSocId: null,
        isActive: false,
        config: '',
    };

    let groupFilter = null;
    if (contractorId) {
        groupFilter = { contractorId: contractorId };
    } else if (defaultContractorId) {
        groupFilter = { contractorId: defaultContractorId };
    }

    return (
        <>
            <RadioButtonGroupInput
                source='newGroup'
                label={false}
                choices={[
                    { id: 'false', name: translate('resources.deviceGroup.fields.existingGroup') },
                    { id: 'true', name: translate('resources.deviceGroup.fields.createNewGroup') },
                ]}
                defaultValue={newGroup ? 'true' : 'false'}
                onChange={event => {
                    const creatingNew = event.target.value === 'true';
                    setNewGroup(creatingNew);
                    if (creatingNew) {
                        setValue('groupId', null, { shouldDirty: true });
                        setValue('group', defaultGroupValues, { shouldDirty: true });
                        resetField('group.file');
                    } else {
                        setValue('groupId', record?.group?.id ?? null, { shouldDirty: true });
                        setValue('group', {}, { shouldDirty: true });
                    }
                }}
            />
            {newGroup ? (
                <>
                    {bulk && <ContractorSelect />}
                    <TextInput
                        source='group.name'
                        label={translate('resources.device.fields.group')}
                        validate={[
                            required(translate('common.validation.required')),
                            maxLength(150, translate('common.validation.maxLength', { count: 150 })),
                            noSpecialCharacters,
                        ]}
                        fullWidth
                    />
                    <Box className='autocomplete-parent'>
                        <ReferenceInput source='group.targetSoftwareMcuId' reference='software-alias' filter={{ 'software.type': 'MCU' }}>
                            <AutocompleteInput
                                filterToQuery={aliasFilterToQuery}
                                fullWidth
                                label={translate('resources.device.labels.mcu')}
                                disablePortal={true}
                            />
                        </ReferenceInput>
                    </Box>
                    <Box className='autocomplete-parent'>
                        <ReferenceInput source='group.targetSoftwareSocId' reference='software-alias' filter={{ 'software.type': 'SOC' }}>
                            <AutocompleteInput
                                filterToQuery={aliasFilterToQuery}
                                fullWidth
                                label={translate('resources.device.labels.soc')}
                                disablePortal={true}
                            />
                        </ReferenceInput>
                    </Box>
                    <BooleanInput source='group.isActive' label={translate('resources.deviceGroup.fields.groupIsActive')} size='small' />
                    <TextInput
                        source='group.config'
                        validate={[required(translate('common.validation.required'))]}
                        label={translate('resources.deviceGroup.labels.config')}
                        multiline
                        fullWidth
                    />
                    <FileInput source='group.file' label={translate('resources.deviceGroup.labels.files')}>
                        <FileField source='src' />
                    </FileInput>
                </>
            ) : (
                <Box className='autocomplete-parent'>
                    <ReferenceInput
                        source='groupId'
                        reference='device-group'
                        label={translate('resources.device.fields.group')}
                        perPage={25}
                        filter={groupFilter}
                    >
                        <AutocompleteInput
                            filterToQuery={groupFilterToQuery}
                            validate={bulk ? null : required(translate('common.validation.required'))}
                            fullWidth={true}
                            disablePortal={true}
                            noOptionsText={translate('resources.deviceGroup.fields.noGroupFound')}
                            label={translate('resources.device.fields.group')}
                        />
                    </ReferenceInput>
                </Box>
            )}
        </>
    );
};

const ContractorSelect = () => {
    const { setValue } = useFormContext();
    const translate = useTranslate();
    const handleChange = () => {
        setValue('collectionPointId', null);
        setValue('groupId', null);
    };

    return (
        <Box className='autocomplete-parent'>
            <ReferenceInput source='contractorId' reference='contractor' perPage={25}>
                <AutocompleteInput
                    filterToQuery={contractorFilterToQuery}
                    validate={required(translate('common.validation.required'))}
                    label={translate('common.contractor')}
                    fullWidth
                    onChange={handleChange}
                    disablePortal={true}
                />
            </ReferenceInput>
        </Box>
    );
};

DeviceGroupInput.propTypes = {
    bulk: PropTypes.bool,
    defaultContractorId: PropTypes.string,
};

export default DeviceGroupInput;
